import { experiencesForMemberships } from '@buakei/auth';
import type { Experience, Me, Membership, SignInProvider, Workspace } from '@buakei/types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { DEMO_PERSONAS, DEMO_WORKSPACES } from '@/demo/personas';
import { api, SessionEndedError } from '@/services/api';
import { readFirebaseConfig } from './firebase-config';
import type { IdentityClient } from './identity-client';

export type Session = {
  uid: string;
  displayName: string;
  /** `demo` for the fictitious personas; `firebase` for real accounts (M2-01). */
  provider: 'demo' | 'firebase';
  email?: string | null;
  emailVerified?: boolean;
  signInProvider?: SignInProvider;
  /** Server withheld memberships, e.g. until the email is verified (ADR-008). */
  withheld?: Me['withheld'];
  memberships: Membership[];
  workspaces: Workspace[];
  experiences: Experience[];
  activeWorkspaceId?: string;
};

/** `checking` while a real account is being restored or loaded from the server. */
export type AuthStatus = 'checking' | 'ready';

/**
 * The integration boundary for identity. Client-side session data is for user experience
 * only — the server derives authorisation from verified tokens and trusted membership records.
 */
export type AuthContextValue = {
  session: Session | null;
  status: AuthStatus;
  /** Real-account actions; null when Firebase is not configured (DEMO personas only). */
  accounts: IdentityClient | null;
  /** A message for the sign-in page, e.g. why a session ended. */
  notice: string | null;
  clearNotice: () => void;
  signInAsPersona: (personaId: string) => Session | null;
  signOut: () => void;
  setActiveWorkspace: (workspaceId: string) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const STORAGE_KEY = 'buakei.demo-session';
export const SESSION_ENDED_NOTICE = 'Your session has ended. Sign in again to continue.';

function buildDemoSession(personaId: string): Session | null {
  const persona = DEMO_PERSONAS.find((p) => p.id === personaId);
  if (!persona) return null;
  const workspaceIds = new Set(persona.memberships.map((m) => m.workspaceId));
  const workspaces = DEMO_WORKSPACES.filter((w) => workspaceIds.has(w.id));
  return {
    uid: `demo_${persona.id}`,
    displayName: persona.displayName,
    provider: 'demo',
    memberships: persona.memberships,
    workspaces,
    experiences: experiencesForMemberships(persona.memberships),
    activeWorkspaceId: workspaces.find((w) => w.kind !== 'learning')?.id,
  };
}

/** A real account's session is built only from the server's `/me` response. */
function buildAccountSession(me: Me, previousWorkspaceId?: string): Session {
  const keep = me.workspaces.some((w) => w.id === previousWorkspaceId);
  return {
    uid: me.uid,
    displayName: me.displayName ?? me.email ?? 'Your account',
    provider: 'firebase',
    email: me.email,
    emailVerified: me.emailVerified,
    signInProvider: me.signInProvider,
    withheld: me.withheld,
    memberships: me.memberships,
    workspaces: me.workspaces,
    experiences: experiencesForMemberships(me.memberships),
    activeWorkspaceId: keep
      ? previousWorkspaceId
      : me.workspaces.find((w) => w.kind !== 'learning')?.id,
  };
}

function readStoredPersona(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredPersona(personaId: string | null) {
  try {
    if (personaId) window.sessionStorage.setItem(STORAGE_KEY, personaId);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage may be unavailable (private mode); the session simply won't persist.
  }
}

async function loadDefaultIdentityClient(): Promise<IdentityClient | null> {
  const config = readFirebaseConfig();
  if (!config) return null;
  const { createFirebaseIdentityClient } = await import('./firebase-client');
  return createFirebaseIdentityClient(config);
}

export function AuthProvider({
  children,
  initialPersonaId,
  identityClient,
}: {
  children: ReactNode;
  initialPersonaId?: string;
  /** Tests inject a fake; `null` disables real accounts; omitted loads Firebase if configured. */
  identityClient?: IdentityClient | null;
}) {
  const realAccountsExpected =
    identityClient !== undefined ? !!identityClient : !!readFirebaseConfig();
  const [accounts, setAccounts] = useState<IdentityClient | null>(identityClient ?? null);
  const [status, setStatus] = useState<AuthStatus>(realAccountsExpected ? 'checking' : 'ready');
  const [notice, setNotice] = useState<string | null>(null);
  const [session, setSessionState] = useState<Session | null>(() => {
    const personaId = initialPersonaId ?? readStoredPersona();
    return personaId ? buildDemoSession(personaId) : null;
  });
  // Mirrors `session` synchronously so identity callbacks see the latest value.
  const sessionRef = useRef(session);
  const setSession = useCallback((next: Session | null) => {
    sessionRef.current = next;
    setSessionState(next);
  }, []);

  useEffect(() => {
    if (identityClient !== undefined) return;
    let cancelled = false;
    loadDefaultIdentityClient()
      .then((client) => {
        if (cancelled) return;
        setAccounts(client);
        if (!client) setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setNotice('Account sign-in is unavailable right now. The DEMO remains available.');
        setStatus('ready');
      });
    return () => {
      cancelled = true;
    };
  }, [identityClient]);

  useEffect(() => {
    if (!accounts) return;
    let generation = 0;
    const unsubscribe = accounts.onUserChanged((user) => {
      const run = ++generation;
      if (!user) {
        // Signed out elsewhere, refresh token revoked/expired, or account disabled.
        if (sessionRef.current?.provider === 'firebase') {
          setNotice(SESSION_ENDED_NOTICE);
          setSession(null);
        }
        setStatus('ready');
        return;
      }
      setStatus('checking');
      api
        .me(accounts)
        .then((me) => {
          if (run !== generation) return;
          writeStoredPersona(null);
          setSession(buildAccountSession(me, sessionRef.current?.activeWorkspaceId));
          setNotice(null);
          setStatus('ready');
        })
        .catch((error: unknown) => {
          if (run !== generation) return;
          if (sessionRef.current?.provider === 'firebase') setSession(null);
          setNotice(
            error instanceof SessionEndedError
              ? SESSION_ENDED_NOTICE
              : `You signed in, but your account could not be loaded. ${
                  error instanceof Error ? error.message : ''
                }`.trim(),
          );
          setStatus('ready');
          // Never leave a provider session the server will not honour.
          void accounts.signOut().catch(() => undefined);
        });
    });
    return () => {
      generation = -1;
      unsubscribe();
    };
  }, [accounts, setSession]);

  const signInAsPersona = useCallback(
    (personaId: string) => {
      const next = buildDemoSession(personaId);
      if (next && sessionRef.current?.provider === 'firebase') {
        void accounts?.signOut().catch(() => undefined);
      }
      setSession(next);
      setNotice(null);
      writeStoredPersona(next ? personaId : null);
      return next;
    },
    [accounts, setSession],
  );

  const signOut = useCallback(() => {
    const wasAccount = sessionRef.current?.provider === 'firebase';
    setSession(null);
    setNotice(null);
    writeStoredPersona(null);
    if (wasAccount) void accounts?.signOut().catch(() => undefined);
  }, [accounts, setSession]);

  const setActiveWorkspace = useCallback(
    (workspaceId: string) => {
      const current = sessionRef.current;
      if (current?.workspaces.some((w) => w.id === workspaceId)) {
        setSession({ ...current, activeWorkspaceId: workspaceId });
      }
    },
    [setSession],
  );

  const clearNotice = useCallback(() => setNotice(null), []);

  const value = useMemo(
    () => ({
      session,
      status,
      accounts,
      notice,
      clearNotice,
      signInAsPersona,
      signOut,
      setActiveWorkspace,
    }),
    [session, status, accounts, notice, clearNotice, signInAsPersona, signOut, setActiveWorkspace],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside an auth provider');
  return value;
}
